import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_7d2805e4-0a10-4257-91cf-a5e52b7fdc5d/artifacts/2wr3xi3q_onemore-global-inc-logo.png';

const navItems = [
  { path: '/', key: 'home' },
  { path: '/about', key: 'about' },
  { path: '/leadership', key: 'leadership' },
  { path: '/products', key: 'products' },
  { path: '/business-model', key: 'business_model' },
  { path: '/operations', key: 'operations' },
  { path: '/structure', key: 'structure' },
  { path: '/compliance', key: 'compliance' },
];

export const Header = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border py-2'
          : 'bg-white/80 backdrop-blur-md py-3'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
            <img
              src={LOGO_URL}
              alt="One More Global Inc."
              className="h-10 sm:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.key}-link`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  location.pathname === item.path
                    ? 'text-[#001f54] bg-[hsl(210,33%,96%)]'
                    : 'text-[#334155] hover:text-[#001f54] hover:bg-[hsl(210,33%,98%)]'
                }`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
          </nav>

          {/* Right side: Language + Contact + Mobile menu */}
          <div className="flex items-center gap-2">
            {/* Language Switcher (desktop) */}
            <Button
              onClick={toggleLanguage}
              variant="ghost"
              size="sm"
              className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-[#334155] hover:text-[#001f54] hover:bg-[hsl(210,33%,98%)]"
              data-testid="language-switcher"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase">{language}</span>
            </Button>

            {/* Contact CTA (desktop) */}
            <Link to="/contact" className="hidden sm:block">
              <Button
                data-testid="header-contact-cta"
                className="bg-[#001f54] text-white hover:bg-[#001f54]/90 text-sm h-9 px-4"
              >
                {t('nav.contact')}
              </Button>
            </Link>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger" aria-label="Menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-white p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-border">
                    <img src={LOGO_URL} alt="One More Global Inc." className="h-10 w-auto" />
                  </div>
                  <nav className="flex flex-col p-4 gap-1 flex-1" data-testid="mobile-nav">
                    {navItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        data-testid={`mobile-nav-${item.key}-link`}
                        className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                          location.pathname === item.path
                            ? 'text-[#001f54] bg-[hsl(210,33%,96%)]'
                            : 'text-[#334155] hover:text-[#001f54] hover:bg-[hsl(210,33%,98%)]'
                        }`}
                      >
                        {t(`nav.${item.key}`)}
                      </Link>
                    ))}
                    <Link
                      to="/contact"
                      data-testid="mobile-nav-contact-link"
                      className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                        location.pathname === '/contact'
                          ? 'text-[#001f54] bg-[hsl(210,33%,96%)]'
                          : 'text-[#334155] hover:text-[#001f54] hover:bg-[hsl(210,33%,98%)]'
                      }`}
                    >
                      {t('nav.contact')}
                    </Link>
                    
                    {/* Language Switcher (mobile) */}
                    <div className="mt-4 pt-4 border-t border-border">
                      <Button
                        onClick={toggleLanguage}
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2"
                        data-testid="mobile-language-switcher"
                      >
                        <Globe className="w-4 h-4" />
                        <span>{language === 'en' ? 'English' : 'Español'}</span>
                      </Button>
                    </div>
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
